import { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

interface Language {
  code: string;
  name: string;
  flag: string;
  googleCode: string;
}

const languages: Language[] = [
  { code: "fr", name: "Français", flag: "🇫🇷", googleCode: "fr" },
  { code: "en", name: "English", flag: "🇬🇧", googleCode: "en" },
  { code: "es", name: "Español", flag: "🇪🇸", googleCode: "es" },
  { code: "de", name: "Deutsch", flag: "🇩🇪", googleCode: "de" },
  { code: "it", name: "Italiano", flag: "🇮🇹", googleCode: "it" },
  { code: "pt", name: "Português", flag: "🇵🇹", googleCode: "pt" },
  { code: "ar", name: "العربية", flag: "🇸🇦", googleCode: "ar" },
  { code: "zh", name: "中文", flag: "🇨🇳", googleCode: "zh-CN" },
];

// Type declaration moved to src/types/map.ts

export const LanguageSelector = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(() => {
    // Charger la langue sauvegardée depuis localStorage
    const savedLangCode = localStorage.getItem("qqoq_language");
    if (savedLangCode) {
      const savedLang = languages.find(l => l.code === savedLangCode);
      if (savedLang) return savedLang;
    }
    return languages[0]; // Français par défaut
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isGoogleTranslateLoaded, setIsGoogleTranslateLoaded] = useState(false);

  useEffect(() => {
    // Load Google Translate script
    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);

      window.googleTranslateElementInit = () => {
        new (window as any).google.translate.TranslateElement(
          {
            pageLanguage: "fr",
            includedLanguages: "fr,en,es,de,it,pt,ar,zh-CN",
            autoDisplay: false,
          },
          "google_translate_element"
        );
        setIsGoogleTranslateLoaded(true);
      };
    }
  }, []);

  // Appliquer automatiquement la langue sauvegardée au chargement
  useEffect(() => {
    if (isGoogleTranslateLoaded && selectedLanguage.code !== "fr") {
      // Petit délai pour s'assurer que Google Translate est prêt
      const timer = setTimeout(() => {
        const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
        if (select) {
          select.value = selectedLanguage.googleCode;
          select.dispatchEvent(new Event("change"));
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isGoogleTranslateLoaded, selectedLanguage]);

  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language);
    setIsOpen(false);
    
    // Sauvegarder le choix dans localStorage
    localStorage.setItem("qqoq_language", language.code);

    if (language.code === "fr") {
      // Reset to original language
      const frame = document.querySelector(".goog-te-banner-frame") as HTMLIFrameElement;
      if (frame) {
        const closeButton = frame.contentDocument?.querySelector(".goog-close-link") as HTMLElement;
        closeButton?.click();
      }
      // Try alternative method to restore original
      const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
      if (select) {
        select.value = "fr";
        select.dispatchEvent(new Event("change"));
      }
      // Reload page to clear translation
      window.location.reload();
      return;
    }

    // Trigger Google Translate
    const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
    if (select) {
      select.value = language.googleCode;
      select.dispatchEvent(new Event("change"));
    }
  };

  return (
    <>
      {/* Hidden Google Translate element */}
      <div id="google_translate_element" className="hidden" />
      
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 px-2 py-1 hover:bg-muted transition-colors"
            title="Changer la langue"
          >
            <span className="text-xl leading-none">{selectedLanguage.flag}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-48 bg-popover border border-border shadow-lg z-50"
        >
          {languages.map((language) => (
            <DropdownMenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language)}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent ${
                selectedLanguage.code === language.code ? "bg-accent/50" : ""
              }`}
            >
              <span className="text-xl leading-none">{language.flag}</span>
              <span className="text-sm font-medium">{language.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
